import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Pencil, Printer } from "lucide-react";
import api from "../services/api";
import { fetchProductPrices, fillUnitPrice } from "../utils/productPrice";
import UnitPriceInput from "./UnitPriceInput";
import { isLockedDate, useTransactionLockDate } from "../utils/transactionLock";
import ImportExportBar from "./ImportExportBar";
import InvoicePrintModal from "./InvoicePrintModal";
import SearchableSelect from "./SearchableSelect";
import CustomerSelect, { CustomerScopeFilters, customerLabel, customersInScope } from "./CustomerSelect";
import { availableFor, fetchAvailability, formatQty, stockShortfalls } from "../utils/stockCheck";
import GstDocumentsModal from "./GstDocumentsModal";
import { PortalJsonDownloadButtons, PortalJsonImportButton } from "./GstPortalJson";
import SalesInvoiceReview from "./SalesInvoiceReview";
// import SchemeAdjustment from "./SchemeAdjustment"; // bulk modify schemes: switched off for now
import FullReversalModal from "./FullReversalModal";
import { BulkCreditNoteModal, BulkVehicleModal, GstPrecheckModal } from "./SalesInvoiceBulkActions";
import Pagination from "./Pagination";
import { RECEIVABLES_API, useReceivableLookups, useReceivableSettings } from "./receivables/common";
import {
  TransportFields,
  canGenerateGst,
  gstStatusBadgeClass,
  gstStatusLabel,
  hasEInvoicePdf,
  hasEWayBillPdf,
  toTransportForm,
  toTransportPayload,
} from "./salesInvoiceGst";

const statuses = ["CONFIRMED", "PARTIALLY_DELIVERED", "DELIVERED", "PARTIALLY_RETURNED", "RETURNED", "EXPIRED", "CANCELLED"];

const emptyItem = {
  productId: "",
  quantity: "",
  unitPrice: "",
  warehouseId: "",
  // scheme / cash discount: either the % or the amount drives the line ("percent" | "amount"); the other is worked out
  schemeDiscountPercent: "",
  schemeDiscountAmount: "",
  schemeBy: "percent",
  cashDiscountPercent: "",
  cashDiscountAmount: "",
  cashBy: "percent",
  // GST % keyed in, only when the product has no tax rate in the masters
  cgstPercent: "",
  sgstPercent: "",
  igstPercent: "",
  // net rate per unit (with tax) being typed; applied as extra cash discount when the field is left
  netRate: null,
  // product type with GST based on the MRP: the line takes the MRP and purchase basic rate instead of a unit price
  mrpMode: false,
  mrp: "",
  purchaseBasicRate: "",
  marginPercent: "",
};

const num = (value) => (value === "" || value === null || value === undefined || Number.isNaN(Number(value)) ? 0 : Number(value));
const numOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
const shortNum = (value, digits = 3) => (value ? String(Math.round(value * 10 ** digits) / 10 ** digits) : "");

/**
 * One line, as the server works it out: gross = qty x unit price; scheme discount = gross x scheme % (or the amount); cash discount =
 * (gross - scheme) x cash % (or the amount); taxable = gross - scheme - cash; CGST / SGST / IGST on the taxable value; total =
 * taxable + taxes; net rate = total / qty. {@code tax}: the GST % to use ({fromMaster, interState, cgst, sgst, igst}).
 */
const lineCalc = (item, tax) => {
  // an invoice being edited: the figures stored on the line, as they are, until something on the line is changed
  if (item.savedLine && !item.dirty) return item.savedLine;
  if (item.mrpMode) return mrpLineCalc(item, tax);
  const qty = num(item.quantity);
  const gross = round2(qty * num(item.unitPrice));
  const scheme = item.schemeBy === "amount" ? round2(num(item.schemeDiscountAmount)) : round2((gross * num(item.schemeDiscountPercent)) / 100);
  const afterScheme = round2(gross - scheme);
  const cash = item.cashBy === "amount" ? round2(num(item.cashDiscountAmount)) : round2((afterScheme * num(item.cashDiscountPercent)) / 100);
  const taxable = round2(afterScheme - cash);
  const cgstPercent = tax.interState ? 0 : tax.fromMaster ? tax.cgst : num(item.cgstPercent);
  const sgstPercent = tax.interState ? 0 : tax.fromMaster ? tax.sgst : num(item.sgstPercent);
  const igstPercent = tax.interState ? (tax.fromMaster ? tax.igst : num(item.igstPercent)) : 0;
  const cgst = round2((taxable * cgstPercent) / 100);
  const sgst = round2((taxable * sgstPercent) / 100);
  const igst = round2((taxable * igstPercent) / 100);
  const total = round2(taxable + cgst + sgst + igst);
  return {
    gross,
    scheme,
    schemePercent: gross ? (scheme * 100) / gross : 0,
    afterScheme,
    cash,
    cashPercent: afterScheme ? (cash * 100) / afterScheme : 0,
    taxable,
    cgstPercent,
    sgstPercent,
    igstPercent,
    cgst,
    sgst,
    igst,
    total,
    netRate: qty ? total / qty : 0,
  };
};

/**
 * A line of a product type with GST based on the MRP (as the server prices it): taxable value per unit = MRP / (1 + GST%), GST =
 * that x GST%, distributor margin = (purchase basic rate + GST) x margin %, net selling rate = purchase basic rate + margin + GST.
 */
const mrpLineCalc = (item, tax) => {
  const qty = num(item.quantity);
  const gstPercent = tax.fromMaster
    ? tax.interState
      ? tax.igst
      : tax.cgst + tax.sgst
    : num(item.cgstPercent) + num(item.sgstPercent) + num(item.igstPercent);
  const taxablePerUnit = num(item.mrp) / (1 + gstPercent / 100);
  const gstPerUnit = (taxablePerUnit * gstPercent) / 100;
  const marginPerUnit = ((num(item.purchaseBasicRate) + gstPerUnit) * num(item.marginPercent)) / 100;
  const netPerUnit = num(item.purchaseBasicRate) + marginPerUnit + gstPerUnit;
  const tax_ = round2(gstPerUnit * qty);
  const total = round2(netPerUnit * qty);
  const taxable = round2(total - tax_);
  const cgstPercent = tax.interState ? 0 : gstPercent / 2;
  const sgstPercent = tax.interState ? 0 : gstPercent / 2;
  const igstPercent = tax.interState ? gstPercent : 0;
  const cgst = tax.interState ? 0 : round2(tax_ / 2);
  return {
    gross: taxable,
    scheme: 0,
    schemePercent: 0,
    afterScheme: taxable,
    cash: 0,
    cashPercent: 0,
    taxable,
    cgstPercent,
    sgstPercent,
    igstPercent,
    cgst,
    sgst: tax.interState ? 0 : round2(tax_ - cgst),
    igst: tax.interState ? tax_ : 0,
    total,
    netRate: round2(netPerUnit),
    margin: round2(marginPerUnit * qty),
    gstPerUnit: round2(gstPerUnit),
  };
};

/** The figures stored on an invoice line, in the shape of {@link lineCalc}. */
const savedLineOf = (item) => {
  const gross = round2(num(item.quantity) * num(item.unitPrice));
  const scheme = num(item.schemeDiscountAmount);
  const cash = num(item.cashDiscountAmount);
  const total = num(item.lineTotal);
  return {
    gross,
    scheme,
    schemePercent: num(item.schemeDiscountPercent),
    afterScheme: round2(gross - scheme),
    cash,
    cashPercent: num(item.cashDiscountPercent),
    taxable: num(item.taxableAmount),
    cgstPercent: num(item.cgstPercent),
    sgstPercent: num(item.sgstPercent),
    igstPercent: num(item.igstPercent),
    cgst: num(item.cgstAmount),
    sgst: num(item.sgstAmount),
    igst: num(item.igstAmount),
    total,
    netRate: num(item.quantity) ? total / num(item.quantity) : 0,
  };
};

/** The line's discounts and keyed-in GST % as the server expects them (the driving % or amount only; GST only when typed). */
const linePayload = (item) => {
  const typedTax = item.cgstPercent !== "" || item.sgstPercent !== "" || item.igstPercent !== "";
  return {
    productId: Number(item.productId),
    quantity: Number(item.quantity),
    unitPrice: numOrNull(item.unitPrice),
    warehouseId: item.warehouseId ? Number(item.warehouseId) : null,
    schemeDiscountPercent: item.schemeBy === "percent" ? numOrNull(item.schemeDiscountPercent) : null,
    schemeDiscountAmount: item.schemeBy === "amount" ? numOrNull(item.schemeDiscountAmount) : null,
    cashDiscountPercent: item.cashBy === "percent" ? numOrNull(item.cashDiscountPercent) : null,
    cashDiscountAmount: item.cashBy === "amount" ? numOrNull(item.cashDiscountAmount) : null,
    ...(typedTax ? { cgstPercent: num(item.cgstPercent), sgstPercent: num(item.sgstPercent), igstPercent: num(item.igstPercent) } : {}),
    // a saved line not changed goes back with its stored figures, so nothing on it is worked out again
    ...(item.savedLine && !item.dirty
      ? {
          taxableAmount: item.savedLine.taxable,
          cgstAmount: item.savedLine.cgst,
          sgstAmount: item.savedLine.sgst,
          igstAmount: item.savedLine.igst,
        }
      : {}),
    // MRP-based line: priced from these; no unit price or discounts
    ...(item.mrpMode
      ? { unitPrice: null, schemeDiscountPercent: null, schemeDiscountAmount: null, cashDiscountPercent: null, cashDiscountAmount: null,
          mrp: numOrNull(item.mrp), purchaseBasicRate: numOrNull(item.purchaseBasicRate) }
      : {}),
  };
};

const emptyForm = {
  customerId: "",
  invoiceDate: "",
  voucherTypeId: "",
  items: [{ ...emptyItem }],
  // Beat-wise distributors assign each invoice to a beat (defaults to the customer's beat).
  beatId: "",
  // optional: hand the new invoice to a delivery boy straight away
  deliveryUserId: "",
  loadSheetNumber: "",
};

/** E-way bill only above this invoice value; e-invoice only for a customer with a GSTIN. */
const EWAY_BILL_THRESHOLD = 50000;

const fmt2 = (value) =>
  value === null || value === undefined || value === "" ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (value) => (value === null || value === undefined ? "-" : `${Number(value)}%`);
const numRight = { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

/** The rows that can be priced: a product and a quantity above zero. */
const pricedRows = (items) => items.map((item, index) => ({ item, index })).filter(({ item }) => item.productId && Number(item.quantity) > 0);

const PAGE_SIZES = [20, 50, 100, 200];
/** Rows per page picked at the bottom of the list; "all" = every invoice that matches. */
const DEFAULT_PAGE_SIZE = 50;

const statusBadgeClass = {
  CONFIRMED: "badge-blue",
  PARTIALLY_DELIVERED: "badge-amber",
  DELIVERED: "badge-green",
  PARTIALLY_RETURNED: "badge-amber",
  RETURNED: "badge-red",
  EXPIRED: "badge-red",
  CANCELLED: "badge-red",
};

const SalesInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [voucherTypes, setVoucherTypes] = useState([]);
  // default numbering of keyed-in invoices (distributor settings): label of the "no voucher type" option, voucher type pre-selected
  const [numbering, setNumbering] = useState(null);
  // product types billed with GST based on the MRP, and the distributor's margins
  const [pricingSettings, setPricingSettings] = useState({ gstOnMrpProductTypes: [] });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [statusModalInvoice, setStatusModalInvoice] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [printInvoiceId, setPrintInvoiceId] = useState(null);
  const lockDate = useTransactionLockDate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [creditWarning, setCreditWarning] = useState(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  // the invoice being edited is on a printed loading sheet: its lines cannot be changed
  const [editingLocked, setEditingLocked] = useState(false);
  // after saving: { invoice, eWayApplies, eInvoiceApplies, eWayBill, eInvoice, transport, busy } — the e-way bill / e-invoice step
  const [gstStep, setGstStep] = useState(null);
  const [gstInvoice, setGstInvoice] = useState(null);
  const [reversalInvoice, setReversalInvoice] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showReview, setShowReview] = useState(false);
  const [reviewRunId, setReviewRunId] = useState(null);
  // after an upload: "Proceed to modify schemes?" (the run it asks about) and the open Modify Schemes screen
  // const [schemePromptRunId, setSchemePromptRunId] = useState(null);
  // const [schemeRunId, setSchemeRunId] = useState(null);
  // bulk actions on the selected invoices: GST pre-check before generation, vehicle / distance update, full credit notes
  const [gstPrecheck, setGstPrecheck] = useState(null);
  const [vehicleInvoices, setVehicleInvoices] = useState(null);
  const [creditNoteInvoices, setCreditNoteInvoices] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkBeatId, setBulkBeatId] = useState("");
  // move the selected invoices to one customer
  const [bulkCustomerId, setBulkCustomerId] = useState("");
  const [assigningCustomer, setAssigningCustomer] = useState(false);
  const [assigningBeat, setAssigningBeat] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [fromDateFilter, setFromDateFilter] = useState("");
  const [toDateFilter, setToDateFilter] = useState("");
  // salesman → beats (several allowed) → customer: the invoices of those beats' / that customer's
  const [listScope, setListScope] = useState({ salesmanUserId: "", beatIds: [] });
  const [customerFilter, setCustomerFilter] = useState("");
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const { beats, salesmen, deliveryPersons } = useReceivableLookups(["beats", "salesmen", "deliveryPersons"]);
  // server-side pricing of the lines being entered: HSN, taxable value, CGST / SGST / IGST, HSN summary and totals
  const [preview, setPreview] = useState(null);
  // stock of the popup's products per warehouse; lines the stock cannot cover block the save
  const [stockByProduct, setStockByProduct] = useState({});
  const [previewError, setPreviewError] = useState("");
  const receivableSettings = useReceivableSettings();
  // Customer-wise (default): an invoice follows its customer's beat. Beat-wise: each invoice is assigned to a beat directly.
  const beatWise = receivableSettings?.invoiceCustomerWise === false;

  const customerById = useMemo(() => {
    const map = new Map();
    customers.forEach((c) => map.set(c.id, c));
    return map;
  }, [customers]);
  const beatById = useMemo(() => new Map(beats.map((b) => [b.id, b])), [beats]);

  // The beat (and so the salesman) an invoice is shown under in the mobile app.
  const beatOfInvoice = (invoice) => {
    const customerBeat = customerById.get(invoice.customerId)?.beatId;
    const id = beatWise ? invoice.assignedBeatId ?? customerBeat : customerBeat;
    return id ? beatById.get(id) : null;
  };

  const loadCustomers = async () => {
    try {
      const [customersData, productsData, voucherData] = await Promise.all([
        api.getAll("/api/v1/customers"),
        api.get("/api/v1/products", { size: 1000 }),
        api.get("/api/v1/voucher-types/by-document-type", { documentType: "SALES_INVOICE" }),
      ]);
      setCustomers(customersData?.content || []);
      setProducts(productsData?.content || []);
      setVoucherTypes(Array.isArray(voucherData) ? voucherData : []);
      api.get("/api/v1/sales-invoices/numbering").then(setNumbering).catch(() => setNumbering(null));
      api
        .get("/api/v1/sales-invoices/pricing-settings")
        .then((data) => setPricingSettings(data || { gstOnMrpProductTypes: [] }))
        .catch(() => setPricingSettings({ gstOnMrpProductTypes: [] }));
    } catch (error) {
      toast.error(error.message || "Failed to load customers / products");
    }
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/sales-invoices", {
        page: pageSize === "all" ? 0 : page,
        size: pageSize === "all" ? undefined : pageSize,
        all: pageSize === "all" || undefined,
        status: statusFilter || undefined,
        fromDate: fromDateFilter || undefined,
        toDate: toDateFilter || undefined,
        customerName: customerNameFilter.trim() || undefined,
        salesmanUserId: listScope.salesmanUserId || undefined,
        beatIds: listScope.beatIds.length ? listScope.beatIds.join(",") : undefined,
        customerId: customerFilter || undefined,
        sort: "invoiceNumber,asc",
      });
      const content = data?.content || [];
      setInvoices(content);
      setTotalPages(data?.totalPages ?? 0);
      setTotalElements(data?.totalElements ?? 0);
      // Keep the selection to invoices still listed (e.g. after a status filter change).
      setSelectedIds((prev) => new Set(content.filter((invoice) => prev.has(invoice.id)).map((invoice) => invoice.id)));
    } catch (error) {
      toast.error(error.message || "Failed to load sales invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  // Changing a filter should always jump back to page 0 — otherwise a narrower result set can land you past
  // its last page, silently showing nothing.
  useEffect(() => {
    setPage(0);
  }, [statusFilter, fromDateFilter, toDateFilter, listScope, customerFilter, customerNameFilter, pageSize]);

  useEffect(() => {
    // Debounce the free-text customer name filter so we don't fire a request on every keystroke.
    const timeout = setTimeout(loadInvoices, customerNameFilter ? 400 : 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, fromDateFilter, toDateFilter, listScope, customerFilter, customerNameFilter, page, pageSize]);

  // Re-price the popup's lines (debounced) whenever the customer, date or items change.
  const previewKey = showCreateModal
    ? JSON.stringify([
        formData.customerId,
        formData.invoiceDate,
        formData.items.map((i) => JSON.stringify({ ...linePayload(i), netRate: undefined })),
      ])
    : "";
  useEffect(() => {
    if (!showCreateModal) return undefined;
    const rows = pricedRows(formData.items);
    if (!formData.customerId || rows.length === 0) {
      setPreview(null);
      setPreviewError("");
      return undefined;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        const result = await api.post("/api/v1/sales-invoices/preview", {
          customerId: Number(formData.customerId),
          invoiceDate: formData.invoiceDate || new Date().toISOString().slice(0, 10),
          items: rows.map(({ item }) => linePayload(item)),
        });
        if (!cancelled) {
          setPreview({ ...result, rowIndexes: rows.map(({ index }) => index) });
          setPreviewError("");
        }
      } catch (error) {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(error.message || "Could not work out the taxes");
        }
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  const stockProductKey = showCreateModal ? [...new Set(formData.items.map((i) => i.productId).filter(Boolean))].sort().join(",") : "";
  useEffect(() => {
    if (!stockProductKey) {
      setStockByProduct({});
      return undefined;
    }
    let cancelled = false;
    fetchAvailability(stockProductKey.split(","))
      .then((stock) => !cancelled && setStockByProduct(stock))
      .catch(() => !cancelled && setStockByProduct({}));
    return () => {
      cancelled = true;
    };
  }, [stockProductKey]);
  const shortfalls = showCreateModal && !editingInvoiceId ? stockShortfalls(formData.items, stockByProduct) : [];

  /** The preview line of form row `index`, if it was priced. */
  const previewLine = (index) => {
    if (!preview) return null;
    const at = preview.rowIndexes.indexOf(index);
    return at >= 0 ? preview.lines[at] : null;
  };

  // beat and salesman the invoice falls under: the picked beat (beat-wise) or the customer's beat
  const formBeat = (() => {
    const id = beatWise && formData.beatId ? Number(formData.beatId) : customerById.get(Number(formData.customerId))?.beatId;
    return id ? beatById.get(id) : null;
  })();

  const openCreate = () => {
    setEditingInvoiceId(null);
    setEditingLocked(false);
    setGstStep(null);
    setFormData({ ...emptyForm, voucherTypeId: numbering?.defaultVoucherTypeId ? String(numbering.defaultVoucherTypeId) : "" });
    setCreditWarning(null);
    setShowCreateModal(true);
  };

  const openEdit = (invoice) => {
    setEditingInvoiceId(invoice.id);
    setEditingLocked(Boolean(invoice.loadingSheetPrintedAt && receivableSettings?.lockInvoiceAfterLoadingSheet));
    setGstStep(null);
    setCreditWarning(null);
    setFormData({
      customerId: String(invoice.customerId),
      invoiceDate: invoice.invoiceDate,
      voucherTypeId: invoice.voucherTypeId ? String(invoice.voucherTypeId) : "",
      // cash discount and round off of the whole bill (saved on the invoice, kept when it is edited)
      billDiscountAmount: num(invoice.billDiscountAmount),
      roundOffAmount: num(invoice.roundOffAmount),
      items: (invoice.items || []).map((item) => ({
        ...emptyItem,
        productId: String(item.productId),
        quantity: String(item.quantity),
        unitPrice: String(item.unitPrice ?? ""),
        warehouseId: item.warehouseId ? String(item.warehouseId) : "",
        // the saved scheme discount as an amount (its % is worked out), so the line comes back exactly as it was
        schemeDiscountPercent: String(item.schemeDiscountPercent ?? item.discountPercent ?? ""),
        schemeDiscountAmount: Number(item.schemeDiscountAmount) ? String(item.schemeDiscountAmount) : "",
        schemeBy: Number(item.schemeDiscountAmount) ? "amount" : "percent",
        // the line keeps the GST % saved on it (an imported bill's own), applied on the new taxable value when a discount changes
        ...(item.gstOnMrp || (item.cgstPercent === null && item.sgstPercent === null && item.igstPercent === null)
          ? {}
          : {
              savedTax: true,
              fixedTax: Boolean(item.taxRateId),
              savedLine: savedLineOf(item),
              dirty: false,
              cgstPercent: String(item.cgstPercent ?? 0),
              sgstPercent: String(item.sgstPercent ?? 0),
              igstPercent: String(item.igstPercent ?? 0),
            }),
        // the saved cash discount is kept as an amount, so the line comes back exactly as it was
        cashDiscountAmount: Number(item.cashDiscountAmount) ? String(item.cashDiscountAmount) : "",
        cashBy: Number(item.cashDiscountAmount) ? "amount" : "percent",
        mrpMode: Boolean(item.gstOnMrp),
        mrp: item.gstOnMrp ? String(item.mrp ?? "") : "",
        purchaseBasicRate: item.gstOnMrp ? String(item.purchaseBasicRate ?? "") : "",
        marginPercent: item.gstOnMrp ? String(item.distributorMarginPercent ?? "") : "",
      })),
      beatId: invoice.assignedBeatId ? String(invoice.assignedBeatId) : "",
      deliveryUserId: "",
      loadSheetNumber: "",
    });
    setShowCreateModal(true);
    // the price dropdowns of the existing rows; their saved prices stay as they are
    (invoice.items || []).forEach((item, index) => fillUnitPrice(setFormData, index, String(item.productId), "sales", true));
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingInvoiceId(null);
    setEditingLocked(false);
    setGstStep(null);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Beat-wise, start from the customer's beat; the user can pick another.
      if (name === "customerId" && beatWise && !prev.beatId) {
        const customerBeat = customerById.get(Number(value))?.beatId;
        if (customerBeat) next.beatId = String(customerBeat);
      }
      return next;
    });
    if (name === "customerId") checkCredit(value);
  };

  const checkCredit = async (customerId) => {
    setCreditWarning(null);
    if (!customerId) return;
    try {
      const result = await api.get(`/api/v1/customers/${customerId}/credit-check`);
      if (result?.warning) setCreditWarning(result.message);
    } catch (error) {
      // Non-blocking — don't interrupt invoice creation if the check itself fails.
    }
  };

  /** Whether a product's type is billed with GST based on the MRP. */
  const isMrpProduct = (product) =>
    Boolean(product?.category) &&
    (pricingSettings.gstOnMrpProductTypes || []).some((type) => String(type).toLowerCase() === String(product.category).trim().toLowerCase());

  const handleItemChange = (index, field, value) => {
    const product = field === "productId" ? products.find((p) => String(p.id) === String(value)) : null;
    const mrpProduct = field === "productId" && isMrpProduct(product);
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      // anything that changes the money on the line: from now on it is worked out from what is on screen
      if (field !== "warehouseId" && field !== "netRate") items[index].dirty = true;
      // a discount typed as a % or as an amount drives the line; the other one is worked out
      if (field === "schemeDiscountPercent") items[index].schemeBy = "percent";
      if (field === "schemeDiscountAmount") items[index].schemeBy = "amount";
      if (field === "cashDiscountPercent") items[index].cashBy = "percent";
      if (field === "cashDiscountAmount") items[index].cashBy = "amount";
      // a newly picked product brings its own price (its base unit price, else its price list's) and tax
      if (field === "productId") {
        items[index].unitPrice = Number(product?.baseUnitPrice) > 0 ? String(product.baseUnitPrice) : "";
        items[index].priceOptions = [];
        items[index].warehouseId = "";
        items[index].cgstPercent = "";
        items[index].sgstPercent = "";
        items[index].igstPercent = "";
        items[index].savedTax = false;
        items[index].fixedTax = false;
        items[index].netRate = null;
        // MRP-based product type: the MRP and the margin come from the product (margin: else the distributor's)
        items[index].mrpMode = mrpProduct;
        items[index].mrp = mrpProduct && product?.mrp1 ? String(product.mrp1) : "";
        items[index].purchaseBasicRate = "";
        items[index].marginPercent = mrpProduct
          ? String(product?.distributorMarginPercent ?? pricingSettings.distributorMarginPercent ?? 0)
          : "";
      }
      return { ...prev, items };
    });
    if (field === "productId" && mrpProduct) {
      // the purchase basic rate starts from the product's newest purchase price
      fetchProductPrices(value, "purchase")
        .then((prices) =>
          setFormData((prev) => {
            const items = [...prev.items];
            if (!items[index] || String(items[index].productId) !== String(value) || items[index].purchaseBasicRate !== "") return prev;
            items[index] = { ...items[index], purchaseBasicRate: prices.length ? String(prices[0]) : "" };
            return { ...prev, items };
          })
        )
        .catch(() => {});
    } else if (field === "productId") fillUnitPrice(setFormData, index, value, "sales", Number(product?.baseUnitPrice) > 0);
  };

  /** The GST % of form row {@code index}: from the masters (read-only) when the server found a rate, else the % typed in. */
  const editLine = (index) =>
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], editing: true };
      return { ...prev, items };
    });

  const taxOf = (index) => {
    const saved = formData.items[index];
    if (saved?.savedTax) {
      // an invoice being edited: what is saved on the line decides, whatever the masters or the customer's state say now
      const interState = num(saved.igstPercent) > 0;
      if (!saved.fixedTax) return { fromMaster: false, interState };
      return { fromMaster: true, interState, cgst: num(saved.cgstPercent), sgst: num(saved.sgstPercent), igst: num(saved.igstPercent) };
    }
    const line = previewLine(index);
    const interState = line ? Boolean(line.interState) : Boolean(preview?.interState);
    if (line?.taxFromMaster) {
      return { fromMaster: true, interState, cgst: num(line.cgstPercent), sgst: num(line.sgstPercent), igst: num(line.igstPercent) };
    }
    return { fromMaster: false, interState };
  };

  /**
   * A net rate per unit (with tax) typed in: the line is brought to exactly that rate by its cash discount — the gap to the worked
   * out rate, less the tax that no longer falls on it, becomes (or adjusts) the cash discount of the line.
   */
  const applyNetRate = (index) => {
    const item = formData.items[index];
    if (item.netRate === null || item.netRate === undefined) return;
    const clear = () => handleItemChange(index, "netRate", null);
    const qty = num(item.quantity);
    const wanted = num(item.netRate);
    if (!qty || !wanted) return clear();
    const tax = taxOf(index);
    const calc = lineCalc(item, tax);
    if (Math.abs(wanted - calc.netRate) < 0.005) return clear();
    const taxPercent = calc.cgstPercent + calc.sgstPercent + calc.igstPercent;
    const taxable = round2((wanted * qty) / (1 + taxPercent / 100));
    const cash = round2(calc.afterScheme - taxable);
    if (cash < 0) {
      toast.warn(`Net rate ₹${wanted} is above the rate without a cash discount (₹${round2(lineCalc({ ...item, cashBy: "amount", cashDiscountAmount: 0 }, tax).netRate)}); change the unit price instead`);
      return clear();
    }
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], cashBy: "amount", cashDiscountAmount: cash.toFixed(2), netRate: null, dirty: true };
      return { ...prev, items };
    });
  };

  const handleTransportChange = (e) => {
    const { name, value } = e.target;
    setGstStep((prev) => ({ ...prev, transport: { ...prev.transport, [name]: value } }));
  };

  const addItemRow = () => setFormData((prev) => ({ ...prev, items: [...prev.items, { ...emptyItem }] }));
  const removeItemRow = (index) => setFormData((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId) {
      toast.error("Please select a customer");
      return;
    }
    if (shortfalls.length) {
      toast.error(`Stock not available — ${shortfalls.join("; ")}. Pick a warehouse that has it or reduce the quantity.`);
      return;
    }
    for (const [index, item] of formData.items.entries()) {
      const calc = lineCalc(item, taxOf(index));
      if (calc.cash > calc.afterScheme + 0.001 || calc.scheme > calc.gross + 0.001) {
        toast.error(`Line ${index + 1}: the discount is more than the line amount`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        customerId: Number(formData.customerId),
        invoiceDate: formData.invoiceDate,
        voucherTypeId: formData.voucherTypeId ? Number(formData.voucherTypeId) : null,
        beatId: beatWise && formData.beatId ? Number(formData.beatId) : null,
        // the invoice amount is the sum of the lines and is all outstanding; payments are recorded on the payments page
        amountCollected: editingInvoiceId ? null : 0,
        balanceAmount: null,
        billDiscountAmount: num(formData.billDiscountAmount),
        items: formData.items.map(linePayload),
        // e-invoice / e-way bill and their transport details are on the next step
        transport: null,
        generateGstDocuments: false,
      };
      const saved = editingInvoiceId
        ? await api.put(`/api/v1/sales-invoices/${editingInvoiceId}`, payload)
        : await api.post("/api/v1/sales-invoices", payload);
      toast.success(editingInvoiceId ? "Sales invoice updated" : "Sales invoice created");
      if (!editingInvoiceId && formData.deliveryUserId && saved?.id) {
        try {
          await api.post(`${RECEIVABLES_API}/delivery-assignments`, {
            deliveryUserId: Number(formData.deliveryUserId),
            invoiceIds: [saved.id],
            loadSheetNumber: formData.loadSheetNumber.trim() || null,
            loadingDate: formData.invoiceDate || null,
          });
          const deliveryName = deliveryPersons.find((p) => String(p.userId) === String(formData.deliveryUserId))?.name;
          toast.success(`Assigned to ${deliveryName || "the delivery boy"} for delivery`);
        } catch (error) {
          toast.warn(`Invoice saved, but it could not be assigned for delivery: ${error.message}. Assign it from Delivery Assignment.`);
        }
      }
      loadInvoices();
      // next step: e-way bill (above ₹50,000) and e-invoice (customer with a GSTIN), when either applies and is not live yet
      const customer = customerById.get(Number(formData.customerId));
      const eWayApplies = Number(saved?.totalAmount) > EWAY_BILL_THRESHOLD && !["GENERATED"].includes(saved?.gst?.eWayBill?.status);
      const eInvoiceApplies = Boolean((saved?.customerGstin || customer?.gstin || "").trim()) && !["GENERATED"].includes(saved?.gst?.eInvoice?.status);
      if (saved?.id && (eWayApplies || eInvoiceApplies)) {
        setGstStep({
          invoice: saved,
          eWayApplies,
          eInvoiceApplies,
          eWayBill: false,
          eInvoice: false,
          transport: toTransportForm(saved?.gst?.transport),
          busy: false,
        });
      } else {
        closeModal();
      }
    } catch (error) {
      toast.error(error.message || (editingInvoiceId ? "Failed to update sales invoice" : "Failed to create sales invoice"));
    } finally {
      setSaving(false);
    }
  };

  /** Step 2: the e-invoice / e-way bill picked are generated; the transport details are saved on the invoice. */
  const handleGstStep = async (e) => {
    e.preventDefault();
    const step = gstStep;
    const transport = toTransportPayload(step.transport);
    if (!step.eWayBill && !step.eInvoice) {
      // nothing to generate now; transport details typed in are still kept on the invoice
      if (transport) {
        try {
          await api.put(`/api/v1/sales-invoices/${step.invoice.id}/gst/transport`, transport);
        } catch (error) {
          toast.error(error.message || "Could not save the transport details");
          return;
        }
      }
      closeModal();
      return;
    }
    if (step.eWayBill && !transport) {
      toast.error("Enter the transport details (vehicle number and distance) for the e-way bill");
      return;
    }
    setGstStep((prev) => ({ ...prev, busy: true }));
    try {
      const gst = await api.post(`/api/v1/sales-invoices/${step.invoice.id}/gst/generate-selected`, {
        eInvoice: step.eInvoice,
        eWayBill: step.eWayBill,
        transport,
      });
      reportGeneration({ gst });
      loadInvoices();
      closeModal();
    } catch (error) {
      toast.error(error.message || "E-invoice / e-way bill could not be generated");
      setGstStep((prev) => (prev ? { ...prev, busy: false } : prev));
    }
  };

  // Generation runs after the invoice is saved; a GSTZen rejection leaves the invoice in place with the document marked failed.
  const reportGeneration = (invoice) => {
    const docs = [invoice?.gst?.eInvoice, invoice?.gst?.eWayBill].filter((doc) => doc && doc.status !== "NOT_REQUIRED");
    if (docs.length === 0) toast.info("No e-invoice / e-way bill is applicable for this invoice");
    else if (docs.some((doc) => doc.status !== "GENERATED"))
      toast.warn("E-invoice / e-way bill not fully generated; open GST on the invoice to see why and retry");
    else toast.success("E-invoice / e-way bill generated");
  };

  // ---- selection and bulk GST actions

  const selectedInvoices = invoices.filter((invoice) => selectedIds.has(invoice.id));
  const allSelected = invoices.length > 0 && selectedInvoices.length === invoices.length;
  const generatableSelected = selectedInvoices.filter(canGenerateGst);
  const eInvoicePdfSelected = selectedInvoices.filter(hasEInvoicePdf);
  const eWayBillPdfSelected = selectedInvoices.filter(hasEWayBillPdf);

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(invoices.map((invoice) => invoice.id)));

  // every check the GST portal would refuse an invoice for is run and shown first; only the invoices that pass are generated
  const handleBulkGenerate = () => {
    if (generatableSelected.length === 0) return;
    setGstPrecheck(generatableSelected);
  };

  const handleGenerated = (results) => {
    setGstPrecheck(null);
    setBulkResults(results);
    loadInvoices();
  };

  const openPdf = async (path, params) => {
    try {
      await api.openFile(path, params);
    } catch (error) {
      toast.error(error.message || "Failed to open PDF");
    }
  };

  const printSelected = (kind, list) =>
    openPdf("/api/v1/sales-invoices/gst-bulk/pdf", { ids: list.map((invoice) => invoice.id).join(","), kind });

  const handleAssignBeat = async () => {
    if (!bulkBeatId) {
      toast.error("Choose a beat");
      return;
    }
    setAssigningBeat(true);
    try {
      const result = await api.put("/api/v1/sales-invoices/beat", {
        invoiceIds: selectedInvoices.map((invoice) => invoice.id),
        beatId: Number(bulkBeatId),
      });
      toast.success(result?.message || "Beat assigned");
      setBulkBeatId("");
      loadInvoices();
    } catch (error) {
      toast.error(error.message || "Failed to assign beat");
    } finally {
      setAssigningBeat(false);
    }
  };

  const handleAssignCustomer = async () => {
    const customer = customerById.get(Number(bulkCustomerId));
    if (!customer) {
      toast.error("Choose the customer");
      return;
    }
    if (!window.confirm(`Move ${selectedInvoices.length} invoice(s), with their payments and credit / debit notes, to ${customer.name}?`)) return;
    setAssigningCustomer(true);
    try {
      const results = await api.put("/api/v1/sales-invoices/customer", {
        invoiceIds: selectedInvoices.map((invoice) => invoice.id),
        customerId: customer.id,
      });
      const failed = (results || []).filter((r) => !r.success);
      if (failed.length) toast.warn(`Not moved: ${failed.map((r) => `${r.invoiceNumber || r.invoiceId} (${r.message})`).join("; ")}`, { autoClose: false });
      const moved = (results || []).length - failed.length;
      if (moved) toast.success(`${moved} invoice(s) now with ${customer.name}`);
      setBulkCustomerId("");
      loadInvoices();
    } catch (error) {
      toast.error(error.message || "Could not change the customer");
    } finally {
      setAssigningCustomer(false);
    }
  };

  const openStatusModal = (invoice) => {
    setStatusModalInvoice(invoice);
    setNewStatus(invoice.status);
    setRemarks("");
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/api/v1/sales-invoices/${statusModalInvoice.id}/status`, {
        status: newStatus,
        remarks: remarks || null,
      });
      toast.success("Invoice status updated");
      setStatusModalInvoice(null);
      loadInvoices();
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading sales invoices...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Customer name"
          value={customerNameFilter}
          onChange={(e) => setCustomerNameFilter(e.target.value)}
          style={{ width: "160px" }}
        />
        <CustomerScopeFilters
          scope={listScope}
          onChange={(scope) => {
            setListScope(scope);
            setCustomerFilter("");
          }}
          salesmen={salesmen}
          beats={beats}
          compact
        />
        <div style={{ minWidth: 240 }}>
          <SearchableSelect
            value={customerFilter}
            onChange={setCustomerFilter}
            options={[
              { value: "", label: "All customers" },
              ...customersInScope(customers, beats, listScope).map((c) => ({ value: c.id, label: customerLabel(c), sublabel: c.phone || "" })),
            ]}
            placeholder={`All customers${listScope.salesmanUserId || listScope.beatIds.length ? " of the beats" : ""}`}
          />
        </div>
        <input
          type="date"
          value={fromDateFilter}
          max={toDateFilter || undefined}
          onChange={(e) => setFromDateFilter(e.target.value)}
          onClick={(e) => e.target.showPicker?.()}
          aria-label="From date"
          title="From date"
        />
        <input
          type="date"
          value={toDateFilter}
          min={fromDateFilter || undefined}
          onChange={(e) => setToDateFilter(e.target.value)}
          onClick={(e) => e.target.showPicker?.()}
          aria-label="To date"
          title="To date"
        />
        {fromDateFilter || toDateFilter || listScope.salesmanUserId || listScope.beatIds.length || customerFilter || customerNameFilter ? (
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setFromDateFilter("");
              setToDateFilter("");
              setListScope({ salesmanUserId: "", beatIds: [] });
              setCustomerFilter("");
              setCustomerNameFilter("");
            }}
          >
            Clear filters
          </button>
        ) : null}
        <button className="primary" onClick={openCreate}>
          + New Sales Invoice
        </button>
        <ImportExportBar
          importPath="/api/v1/sales-invoices/import"
          pdfImportPath="/api/v1/sales-invoices/import-pdf"
          exportPath="/api/v1/sales-invoices/export"
          exportFilename="sales-invoices.xlsx"
          extraParams={{ status: statusFilter || undefined }}
          label="Invoices"
          onImported={(result) => {
            // an import can create new customers, so the customer list is reloaded along with the invoices
            loadInvoices();
            loadCustomers();
            // then show what the upload pushed over a credit limit, the outstanding-bill limit or the stock on hand
            if (result?.successCount > 0) {
              setReviewRunId(result.importRunId ?? null);
              setShowReview(true);
              // if (result.importRunId) {
              // setSchemePromptRunId(result.importRunId);
              // setSchemeRunId(null);
              // }
            }
          }}
        />
        <PortalJsonImportButton onImported={loadInvoices} />
        <button
          type="button"
          className="secondary"
          onClick={() => setShowReview((open) => !open)}
          title="Customers over their credit limit or outstanding-bill limit, and products short of stock"
        >
          {showReview ? "Hide Review" : "Review Limits & Stock"}
        </button>
      </div>

      {/* bulk modify schemes after an import, switched off for now:
      {schemePromptRunId && !schemeRunId ? (
        <div className="content-card" style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <strong>Import completed. Proceed to modify schemes?</strong>
          <span className="page-subtitle">Reduce the scheme discount of the uploaded bills for good and bad customers.</span>
          <button
            className="primary"
            onClick={() => {
              setSchemeRunId(schemePromptRunId);
              setSchemePromptRunId(null);
            }}
          >
            Yes
          </button>
          <button className="secondary" onClick={() => setSchemePromptRunId(null)}>
            No
          </button>
        </div>
      ) : null}
      {schemeRunId ? <SchemeAdjustment importRunId={schemeRunId} onClose={() => setSchemeRunId(null)} onApplied={loadInvoices} /> : null}
      */}
      {showReview ? <SalesInvoiceReview importRunId={reviewRunId} onChanged={loadInvoices} onClose={() => setShowReview(false)} /> : null}

      {selectedInvoices.length > 0 ? (
        <div className="filters-bar" style={{ alignItems: "center" }}>
          <span style={{ fontSize: "13px", fontWeight: 600 }}>{selectedInvoices.length} selected</span>
          <button
            className="primary"
            onClick={handleBulkGenerate}
            disabled={Boolean(gstPrecheck) || generatableSelected.length === 0}
            title={generatableSelected.length === 0 ? "E-invoice / e-way bill already generated or not applicable for the selected invoices" : ""}
          >
            {gstPrecheck ? "Checking..." : `Generate E-Invoice / E-Way Bill (${generatableSelected.length})`}
          </button>
          <button
            className="secondary"
            onClick={() => printSelected("e-invoice", eInvoicePdfSelected)}
            disabled={eInvoicePdfSelected.length === 0}
            style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}
          >
            <Printer size={13} /> Print E-Invoices ({eInvoicePdfSelected.length})
          </button>
          <button
            className="secondary"
            onClick={() => printSelected("e-way-bill", eWayBillPdfSelected)}
            disabled={eWayBillPdfSelected.length === 0}
            style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}
          >
            <Printer size={13} /> Print E-Way Bills ({eWayBillPdfSelected.length})
          </button>
          <button className="secondary" onClick={() => setVehicleInvoices(selectedInvoices)} title="Vehicle number and approximate distance for the e-way bills">
            Vehicle / Distance
          </button>
          <button
            className="secondary"
            onClick={() => setCreditNoteInvoices(selectedInvoices)}
            title="Full-amount credit note for each selected invoice (e.g. billed to the wrong customer)"
          >
            Bulk Credit Note
          </button>
          <PortalJsonDownloadButtons selectedInvoices={selectedInvoices} />
          <div style={{ minWidth: 220 }}>
            <SearchableSelect
              value={bulkCustomerId}
              onChange={setBulkCustomerId}
              options={customers.map((c) => ({ value: c.id, label: customerLabel(c), sublabel: c.phone || "" }))}
              placeholder="Assign to customer..."
            />
          </div>
          <button className="secondary" onClick={handleAssignCustomer} disabled={!bulkCustomerId || assigningCustomer}>
            {assigningCustomer ? "Moving..." : "Assign Customer"}
          </button>
          {beatWise ? (
            <>
              <select value={bulkBeatId} onChange={(e) => setBulkBeatId(e.target.value)} aria-label="Beat to assign">
                <option value="">Assign to beat...</option>
                {beats.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                    {b.salesmanName ? ` (${b.salesmanName})` : ""}
                  </option>
                ))}
              </select>
              <button className="secondary" onClick={handleAssignBeat} disabled={!bulkBeatId || assigningBeat}>
                {assigningBeat ? "Assigning..." : "Assign Beat"}
              </button>
            </>
          ) : null}
          <button className="secondary" onClick={() => setSelectedIds(new Set())}>
            Clear selection
          </button>
        </div>
      ) : null}

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all invoices" />
              </th>
              <th>ID</th>
              <th>Invoice #</th>
              <th>Customer</th>
              <th title={beatWise ? "Beat assigned to the invoice" : "Customer's beat"}>Beat / Salesman</th>
              <th>Date</th>
              <th title="Invoice total including tax">Total Amount</th>
              {/* <th>Collected</th> */}
              <th>Balance</th>
              {/* <th>Charges</th>
              <th>Status</th> */}
              <th>Source</th>
              <th>E-Invoice</th>
              <th>E-Way Bill</th>
              <th className="sticky-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={12}>No sales invoices found.</td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(invoice.id)}
                      onChange={() => toggleSelected(invoice.id)}
                      aria-label={`Select invoice ${invoice.invoiceNumber}`}
                    />
                  </td>
                  <td>{invoice.id}</td>
                  <td>{invoice.invoiceNumber}</td>
                  <td>{invoice.customerName || customerById.get(invoice.customerId)?.name || invoice.customerId}</td>
                  <td>
                    {(() => {
                      const beat = beatOfInvoice(invoice);
                      if (!beat) return <span className="badge badge-amber">No beat</span>;
                      return (
                        <>
                          {beat.name}
                          {beat.salesmanName ? (
                            <span style={{ display: "block", fontSize: "11px", color: "var(--color-muted, #667085)" }}>{beat.salesmanName}</span>
                          ) : null}
                        </>
                      );
                    })()}
                  </td>
                  <td>{invoice.invoiceDate}</td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt2(invoice.totalAmount)}</td>
                  {/* <td>{invoice.amountCollected}</td> */}
                  <td>{invoice.balanceAmount ?? "-"}</td>
                  {/* <td>{Number(invoice.additionalCharges) ? invoice.additionalCharges : "-"}</td>
                  <td>
                    <span className={`badge ${statusBadgeClass[invoice.status] || "badge-gray"}`}>{invoice.status}</span>
                  </td> */}
                  <td>{invoice.source || "MANUAL"}</td>
                  <td>
                    <GstCell
                      doc={invoice.gst?.eInvoice}
                      number={invoice.gst?.eInvoice?.ackNo}
                      canPrint={hasEInvoicePdf(invoice)}
                      onPrint={() => openPdf(`/api/v1/sales-invoices/${invoice.id}/gst/e-invoice/pdf`)}
                    />
                  </td>
                  <td>
                    <GstCell
                      doc={invoice.gst?.eWayBill}
                      number={invoice.gst?.eWayBill?.ewayBillNumber}
                      canPrint={hasEWayBillPdf(invoice)}
                      onPrint={() => openPdf(`/api/v1/sales-invoices/${invoice.id}/gst/e-way-bill/pdf`)}
                    />
                  </td>
                  <td className="action-buttons sticky-actions">
                    {isLockedDate(invoice.invoiceDate, lockDate) ? (
                      <span className="badge badge-gray" title={`Dated on or before the transaction lock date (${lockDate}); it can no longer be changed`}>
                        Locked
                      </span>
                    ) : (
                      <>
                        <button
                          className="secondary"
                          onClick={() => openEdit(invoice)}
                          title={invoice.loadingSheetPrintedAt && receivableSettings?.lockInvoiceAfterLoadingSheet ? "Loading sheet printed: the items can no longer be changed" : undefined}
                        >
                          Edit
                        </button>
                        {/* status changes go through Return (credit note) instead
                        <button className="secondary" onClick={() => openStatusModal(invoice)}>
                          Update Status
                        </button> */}
                      </>
                    )}
                    <button
                      className="secondary"
                      onClick={() => setPrintInvoiceId(invoice.id)}
                      style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}
                      title="Print invoice"
                    >
                      <Printer size={13} /> Print
                    </button>
                    <button className="secondary" onClick={() => setGstInvoice(invoice)} title="E-invoice / e-way bill">
                      GST
                    </button>
                    {invoice.status !== "CANCELLED" && invoice.status !== "RETURNED" && !isLockedDate(invoice.invoiceDate, lockDate) ? (
                      <button
                        className="secondary"
                        onClick={() => setReversalInvoice(invoice)}
                        title="Partial return, full return or full cancellation of this invoice"
                      >
                        Return
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {invoices.length ? (
            <tfoot>
              <tr>
                <td colSpan={6} style={{ textAlign: "right", fontWeight: 700 }}>
                  Total of {invoices.length} invoice(s) on this page
                </td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt2(invoices.reduce((sum, inv) => sum + Number(inv.totalAmount || 0), 0))}</td>
                <td style={{ fontWeight: 700 }}>{fmt2(invoices.reduce((sum, inv) => sum + Number(inv.balanceAmount || 0), 0))}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 14 }}>
          Rows per page
          <select
            value={pageSize}
            onChange={(e) => setPageSize(e.target.value === "all" ? "all" : Number(e.target.value))}
            style={{ width: "auto" }}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
            <option value="all">All</option>
          </select>
          {pageSize === "all" ? <span className="page-subtitle">{totalElements} invoice(s)</span> : null}
        </label>
        {pageSize === "all" ? null : (
          <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} />
        )}
      </div>

      {showCreateModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-box modal-xl">
            <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
              <h3 className="text-xl font-semibold">
                {gstStep
                  ? `E-Way Bill / E-Invoice — ${gstStep.invoice.invoiceNumber}`
                  : editingInvoiceId
                  ? "Edit Sales Invoice"
                  : "Create Sales Invoice"}
              </h3>
              <button className="close-btn" onClick={closeModal}>
                Close
              </button>
            </div>

            {gstStep ? (
              <form onSubmit={handleGstStep}>
                <div className="page-subtitle" style={{ marginBottom: 10 }}>
                  Invoice {gstStep.invoice.invoiceNumber} is saved — total ₹{fmt2(gstStep.invoice.totalAmount)}. Tick what to generate now;
                  anything left unticked can be generated later from the invoice list (GST button).
                </div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
                  {gstStep.eWayApplies ? (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={gstStep.eWayBill}
                        onChange={(e) => setGstStep((prev) => ({ ...prev, eWayBill: e.target.checked }))}
                      />
                      Generate e-way bill <span className="page-subtitle">(invoice above ₹50,000)</span>
                    </label>
                  ) : null}
                  {gstStep.eInvoiceApplies ? (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={gstStep.eInvoice}
                        onChange={(e) => setGstStep((prev) => ({ ...prev, eInvoice: e.target.checked }))}
                      />
                      Generate e-invoice <span className="page-subtitle">(customer has a GSTIN)</span>
                    </label>
                  ) : null}
                </div>
                {gstStep.eWayBill || gstStep.eWayApplies ? (
                  <>
                    <h4 style={{ margin: "4px 0", fontWeight: 600 }}>Transport Details {gstStep.eWayBill ? "*" : "(for the e-way bill)"}</h4>
                    <p className="page-subtitle" style={{ marginBottom: 6 }}>
                      By road give the vehicle number and approximate distance; by rail, air or ship the transport document number and
                      date. Saved on the invoice.
                    </p>
                    <TransportFields transport={gstStep.transport} onChange={handleTransportChange} />
                  </>
                ) : null}
                <div className="modal-actions">
                  <button type="button" className="secondary" onClick={closeModal} disabled={gstStep.busy}>
                    Skip — generate later
                  </button>
                  <button type="submit" className="primary" disabled={gstStep.busy}>
                    {gstStep.busy ? "Generating..." : gstStep.eWayBill || gstStep.eInvoice ? "Generate" : "Done"}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleCreateSubmit}>
                {creditWarning ? (
                  <div
                    style={{
                      background: "var(--color-danger-soft, #fdecea)",
                      color: "var(--color-danger, #b42318)",
                      border: "1px solid var(--color-danger, #b42318)",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      marginBottom: "8px",
                      fontSize: "13px",
                    }}
                  >
                    <strong>Credit warning:</strong> {creditWarning} You can still proceed with this invoice.
                  </div>
                ) : null}
                {editingLocked ? (
                  <div className="page-subtitle" style={{ color: "var(--color-warning, #b54708)", marginBottom: 8 }}>
                    The loading sheet of this invoice is printed: its items (quantity, price, discounts, tax) can no longer be changed.
                  </div>
                ) : null}
                {/* header: several fields a row */}
                <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", marginTop: 4, gap: "8px 12px" }}>
                  <div className="form-group" style={{ gridColumn: "span 2" }}>
                    <label>Customer</label>
                    <CustomerSelect
                      customers={customers}
                      value={formData.customerId}
                      onChange={(value) => handleFieldChange({ target: { name: "customerId", value } })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Invoice Date</label>
                    <input
                      type="date"
                      name="invoiceDate"
                      value={formData.invoiceDate}
                      onChange={handleFieldChange}
                      onClick={(e) => e.target.showPicker?.()}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Voucher Type</label>
                    <select name="voucherTypeId" value={formData.voucherTypeId} onChange={handleFieldChange}>
                      <option value="">{numbering?.defaultLabel || "Default (INV numbering)"}</option>
                      {voucherTypes.map((vt) => (
                        <option key={vt.id} value={vt.id}>
                          {vt.name} ({vt.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  {beatWise ? (
                    <div className="form-group">
                      <label>Beat *</label>
                      <select name="beatId" value={formData.beatId} onChange={handleFieldChange} required>
                        <option value="">Select beat</option>
                        {beats.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                            {b.salesmanName ? ` (${b.salesmanName})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Beat / Salesman</label>
                      <input
                        value={formBeat ? `${formBeat.name}${formBeat.salesmanName ? ` / ${formBeat.salesmanName}` : ""}` : formData.customerId ? "No beat" : "-"}
                        readOnly
                        tabIndex={-1}
                      />
                    </div>
                  )}
                  {!editingInvoiceId ? (
                    <div className="form-group">
                      <label>Delivery Boy</label>
                      <select name="deliveryUserId" value={formData.deliveryUserId} onChange={handleFieldChange}>
                        <option value="">Assign later</option>
                        {deliveryPersons.map((p) => (
                          <option key={p.userId} value={p.userId}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                  {!editingInvoiceId && formData.deliveryUserId ? (
                    <div className="form-group">
                      <label>Load Sheet No{receivableSettings?.loadSheetNumberRequired ? " *" : ""}</label>
                      <input
                        name="loadSheetNumber"
                        value={formData.loadSheetNumber}
                        onChange={handleFieldChange}
                        maxLength={50}
                        required={Boolean(receivableSettings?.loadSheetNumberRequired)}
                      />
                    </div>
                  ) : null}
                </div>

                <h4 style={{ marginTop: 10, marginBottom: 4, fontWeight: 600 }}>Items</h4>
                <fieldset disabled={editingLocked} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
                  <div className="table-wrapper" style={{ marginTop: 0, overflowX: "hidden" }}>
                    <table className="items-table fit-items">
                      <colgroup>
                        <col style={{ width: "17%" }} />
                        <col style={{ width: "5.5%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "6.5%" }} />
                        <col style={{ width: "5%" }} />
                        <col style={{ width: "6.5%" }} />
                        <col style={{ width: "5%" }} />
                        <col style={{ width: "6.5%" }} />
                        <col style={{ width: "7%" }} />
                        {(preview?.interState ? ["igst"] : ["cgst", "sgst"]).map((tax) => [
                          <col key={`${tax}p`} style={{ width: "4.5%" }} />,
                          <col key={`${tax}a`} style={{ width: "6%" }} />,
                        ])}
                        <col style={{ width: "7%" }} />
                        <col style={{ width: "7.5%" }} />
                        <col style={{ width: "2.5%" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className="num">Qty</th>
                          <th title="Warehouse the line is billed from; blank = any warehouse that has the stock">Warehouse</th>
                          <th className="num" title="MRP and purchase basic rate on lines of a product type with GST based on MRP">Unit Price / MRP</th>
                          <th className="num" title="Scheme discount % of the gross">Sch %</th>
                          <th className="num">Sch Amt</th>
                          <th className="num" title="Cash discount % of the amount after the scheme discount">Cash %</th>
                          <th className="num">Cash Amt</th>
                          <th className="num">Taxable</th>
                          {preview?.interState ? (
                            <>
                              <th className="num">IGST %</th>
                              <th className="num">IGST Amt</th>
                            </>
                          ) : (
                            <>
                              <th className="num">CGST %</th>
                              <th className="num">CGST Amt</th>
                              <th className="num">SGST %</th>
                              <th className="num">SGST Amt</th>
                            </>
                          )}
                          <th className="num" title="Net rate per unit with tax; type a lower rate to give the difference as cash discount">
                            Net Rate
                          </th>
                          <th className="num">Total</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => {
                          const line = previewLine(index);
                          const tax = taxOf(index);
                          const calc = lineCalc(item, tax);
                          const product = products.find((p) => String(p.id) === String(item.productId));
                          const stock = item.productId ? stockByProduct[item.productId] : null;
                          const short = stock && Number(item.quantity) > availableFor(stock, item.warehouseId);
                          // a saved line opens through its edit icon
                          const lineLocked = Boolean(editingInvoiceId && item.savedLine && !item.editing);
                          const taxCell = (field, percent) =>
                            tax.fromMaster || !item.productId ? (
                              <input className="derived" value={percent ? shortNum(percent) : "0"} readOnly tabIndex={-1} title="From the tax rate in the masters" />
                            ) : (
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                value={item[field]}
                                placeholder="%"
                                title="No tax rate in the masters for this product: enter the GST %"
                                onChange={(e) => handleItemChange(index, field, e.target.value)}
                              />
                            );
                          return (
                            <tr key={index}>
                              <td inert={lineLocked}>
                                <SearchableSelect
                                  value={item.productId}
                                  onChange={(value) => handleItemChange(index, "productId", value)}
                                  options={products.map((p) => ({ value: p.id, label: p.name, sublabel: p.skuCode }))}
                                  placeholder="Search product..."
                                  required
                                />
                                {item.productId ? (
                                  <div className="page-subtitle" style={{ fontSize: 11, marginTop: 2 }}>
                                    HSN {line?.hsnCode || product?.hsnCode || "-"}
                                    {stock ? (
                                      <span style={{ color: short ? "var(--color-danger, #b42318)" : undefined }}>
                                        {" "}· stock {formatQty(availableFor(stock, item.warehouseId))}
                                      </span>
                                    ) : null}
                                  </div>
                                ) : null}
                              </td>
                              <td inert={lineLocked}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.001"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                  required
                                />
                              </td>
                              <td inert={lineLocked}>
                                <select
                                  value={item.warehouseId || ""}
                                  onChange={(e) => handleItemChange(index, "warehouseId", e.target.value)}
                                  disabled={!item.productId}
                                >
                                  <option value="">Any</option>
                                  {(stock?.warehouses || []).map((w) => (
                                    <option key={w.warehouseId} value={w.warehouseId}>
                                      {w.warehouseName}
                                      {w.warehouseType === "BAD" ? " (bad)" : ""} — {formatQty(w.quantity)}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td inert={lineLocked}>
                                {item.mrpMode ? (
                                  <>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.mrp}
                                      placeholder="MRP"
                                      title="MRP (GST included)"
                                      onChange={(e) => handleItemChange(index, "mrp", e.target.value)}
                                      required
                                    />
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.0001"
                                      value={item.purchaseBasicRate}
                                      placeholder="Pur. basic"
                                      title="Purchase basic rate (before tax)"
                                      style={{ marginTop: 3 }}
                                      onChange={(e) => handleItemChange(index, "purchaseBasicRate", e.target.value)}
                                      required
                                    />
                                    <div className="page-subtitle" style={{ fontSize: 10.5 }} title="Distributor margin">
                                      Margin {num(item.marginPercent)}% = ₹{fmt2(calc.margin)}
                                    </div>
                                  </>
                                ) : (
                                  <UnitPriceInput
                                    value={item.unitPrice}
                                    onChange={(value) => handleItemChange(index, "unitPrice", value)}
                                    options={item.priceOptions}
                                    placeholder="price"
                                  />
                                )}
                              </td>
                              <td inert={lineLocked}>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={item.schemeBy === "percent" ? item.schemeDiscountPercent : shortNum(calc.schemePercent)}
                                  onChange={(e) => handleItemChange(index, "schemeDiscountPercent", e.target.value)}
                                  disabled={item.mrpMode}
                                />
                              </td>
                              <td inert={lineLocked}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.schemeBy === "amount" ? item.schemeDiscountAmount : calc.scheme ? calc.scheme.toFixed(2) : ""}
                                  onChange={(e) => handleItemChange(index, "schemeDiscountAmount", e.target.value)}
                                  disabled={item.mrpMode}
                                />
                              </td>
                              <td inert={lineLocked}>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={item.cashBy === "percent" ? item.cashDiscountPercent : shortNum(calc.cashPercent)}
                                  onChange={(e) => handleItemChange(index, "cashDiscountPercent", e.target.value)}
                                  disabled={item.mrpMode}
                                />
                              </td>
                              <td inert={lineLocked}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.cashBy === "amount" ? item.cashDiscountAmount : calc.cash ? calc.cash.toFixed(2) : ""}
                                  onChange={(e) => handleItemChange(index, "cashDiscountAmount", e.target.value)}
                                  disabled={item.mrpMode}
                                />
                              </td>
                              <td className="num">{fmt2(calc.taxable)}</td>
                              {preview?.interState ? (
                                <>
                                  <td inert={lineLocked}>{taxCell("igstPercent", calc.igstPercent)}</td>
                                  <td className="num">{fmt2(calc.igst)}</td>
                                </>
                              ) : (
                                <>
                                  <td inert={lineLocked}>{taxCell("cgstPercent", calc.cgstPercent)}</td>
                                  <td className="num">{fmt2(calc.cgst)}</td>
                                  <td inert={lineLocked}>{taxCell("sgstPercent", calc.sgstPercent)}</td>
                                  <td className="num">{fmt2(calc.sgst)}</td>
                                </>
                              )}
                              <td inert={lineLocked}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.netRate ?? (calc.netRate ? calc.netRate.toFixed(2) : "")}
                                  onChange={(e) => handleItemChange(index, "netRate", e.target.value)}
                                  disabled={item.mrpMode}
                                  onBlur={() => applyNetRate(index)}
                                  title="Net rate per unit with tax. A lower rate typed here is given as cash discount."
                                />
                              </td>
                              <td className="num" style={{ fontWeight: 600 }}>
                                {fmt2(calc.total)}
                              </td>
                              <td>
                                {lineLocked ? (
                                  <button
                                    type="button"
                                    className="secondary"
                                    style={{ padding: "2px 5px" }}
                                    onClick={() => editLine(index)}
                                    title="Edit this line"
                                    aria-label={`Edit line ${index + 1}`}
                                  >
                                    <Pencil size={13} />
                                  </button>
                                ) : formData.items.length > 1 ? (
                                  <button type="button" className="close-btn" style={{ padding: "2px 6px" }} onClick={() => removeItemRow(index)} tabIndex={-1}>
                                    &times;
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" className="secondary" style={{ marginTop: 6, padding: "5px 12px" }} onClick={addItemRow}>
                    + Add Item
                  </button>
                </fieldset>
                {shortfalls.length ? (
                  <div
                    style={{
                      background: "var(--color-danger-soft, #fdecea)",
                      color: "var(--color-danger, #b42318)",
                      border: "1px solid var(--color-danger, #b42318)",
                      borderRadius: 8,
                      padding: "6px 12px",
                      marginTop: 8,
                      fontSize: 13,
                    }}
                  >
                    <strong>Stock not available — the invoice cannot be created:</strong> {shortfalls.join("; ")}. Pick a warehouse that has
                    the stock, or reduce the quantity.
                  </div>
                ) : null}
                {previewError ? (
                  <div className="page-subtitle" style={{ color: "var(--color-danger, #b42318)", marginTop: 6 }}>
                    {previewError}
                  </div>
                ) : null}

                {(() => {
                  // summary from the lines as entered (the server works them out the same way)
                  const calcs = formData.items.map((item, index) => lineCalc(item, taxOf(index)));
                  const sum = (key) => calcs.reduce((total, c) => total + c[key], 0);
                  const interState = Boolean(preview?.interState);
                  const billDiscount = num(formData.billDiscountAmount);
                  const roundOff = num(formData.roundOffAmount);
                  const rows = [
                    ["Gross Amount", sum("gross")],
                    ["Scheme Discount", sum("scheme")],
                    ["Taxable Amount", sum("taxable")],
                    ...(interState ? [["IGST", sum("igst")]] : [["CGST", sum("cgst")], ["SGST", sum("sgst")]]),
                    // all the cash discount: of the lines and of the whole bill
                    ["Cash Discount", sum("cash") + billDiscount],
                    ...(roundOff ? [["Round Off", roundOff]] : []),
                  ];
                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(260px, 1fr)", gap: 14, marginTop: 10, alignItems: "start" }}>
                      <div>
                        {preview?.hsnSummary?.length ? (
                          <>
                            <h4 style={{ marginBottom: 4, fontWeight: 600 }}>Tax Summary (HSN-wise)</h4>
                            <div className="table-wrapper" style={{ marginTop: 0 }}>
                              <table>
                                <thead>
                                  <tr>
                                    <th>HSN Code</th>
                                    <th style={numRight}>Tax %</th>
                                    <th style={numRight}>Taxable Amt</th>
                                    {interState ? (
                                      <th style={numRight}>IGST Amt</th>
                                    ) : (
                                      <>
                                        <th style={numRight}>CGST Amt</th>
                                        <th style={numRight}>SGST Amt</th>
                                      </>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {preview.hsnSummary.map((row) => (
                                    <tr key={`${row.hsnCode}-${row.taxPercent}`}>
                                      <td>{row.hsnCode}</td>
                                      <td style={numRight}>{pct(row.taxPercent)}</td>
                                      <td style={numRight}>{fmt2(row.taxableAmount)}</td>
                                      {interState ? (
                                        <td style={numRight}>{fmt2(row.igstAmount)}</td>
                                      ) : (
                                        <>
                                          <td style={numRight}>{fmt2(row.cgstAmount)}</td>
                                          <td style={numRight}>{fmt2(row.sgstAmount)}</td>
                                        </>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        ) : null}
                      </div>
                      <div className="page-panel" style={{ padding: "8px 14px" }}>
                        {rows.map(([label, value]) =>
                          label === "Cash Discount" ? (
                            // all the cash discount of the bill; what is over the lines' own goes on the bill (sales_invoices)
                            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0" }}>
                              <label htmlFor="bill-cash-discount" title={`Includes the lines' cash discount of ₹${fmt2(sum("cash"))}`}>
                                {label}
                              </label>
                              {formData.cashDiscountEditing ? (
                                <input
                                  id="bill-cash-discount"
                                  type="number"
                                  min={round2(sum("cash"))}
                                  step="0.01"
                                  autoFocus
                                  value={formData.cashDiscountTyped ?? (value ? round2(value) : "")}
                                  onChange={(e) => {
                                    const typed = e.target.value;
                                    const onBill = round2(Math.max(0, num(typed) - sum("cash")));
                                    setFormData((prev) => ({ ...prev, cashDiscountTyped: typed, billDiscountAmount: onBill ? String(onBill) : "" }));
                                  }}
                                  onBlur={() => setFormData((prev) => ({ ...prev, cashDiscountTyped: undefined }))}
                                  style={{ width: 110, textAlign: "right" }}
                                />
                              ) : (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  <span style={numRight}>₹{fmt2(value)}</span>
                                  <button
                                    type="button"
                                    className="secondary"
                                    style={{ padding: "2px 5px" }}
                                    onClick={() => setFormData((prev) => ({ ...prev, cashDiscountEditing: true }))}
                                    title="Edit the cash discount"
                                    aria-label="Edit cash discount"
                                  >
                                    <Pencil size={13} />
                                  </button>
                                </span>
                              )}
                            </div>
                          ) : (
                          <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                            <span>{label}</span>
                            <span style={numRight}>₹{fmt2(value)}</span>
                          </div>
                          )
                        )}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderTop: "1px solid var(--color-border, #e4e7ec)",
                            marginTop: 4,
                            paddingTop: 6,
                            fontWeight: 700,
                            fontSize: 16,
                          }}
                        >
                          <span>Net Invoice Amount</span>
                          <span style={numRight}>₹{fmt2(sum("total") - billDiscount + roundOff)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="modal-actions">
                  <button type="button" className="secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="primary"
                    disabled={saving || shortfalls.length > 0}
                    title={shortfalls.length ? "Stock is not available for some lines" : "Ctrl+Enter saves"}
                  >
                    {saving ? "Saving..." : editingInvoiceId ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}

      {statusModalInvoice ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setStatusModalInvoice(null)}>
          <div className="modal-box" style={{ maxWidth: "480px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Update Status — {statusModalInvoice.invoiceNumber}</h3>
              <button className="close-btn" onClick={() => setStatusModalInvoice(null)}>
                Close
              </button>
            </div>
            <form onSubmit={handleUpdateStatus}>
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <div className="form-group">
                  <label>Status</label>
                  <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                    {statuses.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <input value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setStatusModalInvoice(null)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {gstInvoice ? <GstDocumentsModal invoice={gstInvoice} onClose={() => setGstInvoice(null)} onUpdated={loadInvoices} /> : null}
      {reversalInvoice ? (
        <FullReversalModal allowPartial invoice={reversalInvoice} onClose={() => setReversalInvoice(null)} onDone={loadInvoices} />
      ) : null}

      {gstPrecheck ? (
        <GstPrecheckModal invoices={gstPrecheck} onClose={() => setGstPrecheck(null)} onGenerated={handleGenerated} onChanged={loadInvoices} />
      ) : null}
      {vehicleInvoices ? <BulkVehicleModal invoices={vehicleInvoices} onClose={() => setVehicleInvoices(null)} onDone={loadInvoices} /> : null}
      {creditNoteInvoices ? (
        <BulkCreditNoteModal
          invoices={creditNoteInvoices}
          onClose={() => {
            setCreditNoteInvoices(null);
            setSelectedIds(new Set());
          }}
          onDone={loadInvoices}
        />
      ) : null}

      {bulkResults ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setBulkResults(null)}>
          <div className="modal-box" style={{ maxWidth: "820px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">E-Invoice / E-Way Bill Generation</h3>
              <button className="close-btn" onClick={() => setBulkResults(null)}>
                Close
              </button>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>E-Invoice</th>
                    <th>E-Way Bill</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.map((result) => (
                    <tr key={result.invoiceId}>
                      <td>{result.invoiceNumber || result.invoiceId}</td>
                      <td>
                        <GstCell doc={result.gst?.eInvoice} number={result.gst?.eInvoice?.ackNo} />
                      </td>
                      <td>
                        <GstCell doc={result.gst?.eWayBill} number={result.gst?.eWayBill?.ewayBillNumber} />
                      </td>
                      <td style={{ fontSize: "12px", color: result.success ? "inherit" : "var(--color-danger, #b42318)", maxWidth: "320px" }}>
                        {result.message ||
                          [result.gst?.eInvoice?.errorMessage, result.gst?.eWayBill?.errorMessage].filter(Boolean).join("; ") ||
                          "OK"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button type="button" className="primary" onClick={() => setBulkResults(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {printInvoiceId ? <InvoicePrintModal invoiceId={printInvoiceId} onClose={() => setPrintInvoiceId(null)} /> : null}
    </div>
  );
};

// Status of one GST document in a table cell, with its number and a print shortcut once the PDF is stored.
const GstCell = ({ doc, number, canPrint, onPrint }) => {
  const status = doc?.status || "NOT_REQUIRED";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px", alignItems: "flex-start" }} title={doc?.errorMessage || ""}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
        <span className={`badge ${gstStatusBadgeClass[status] || "badge-gray"}`}>{gstStatusLabel[status] || status}</span>
        {canPrint ? (
          <button
            type="button"
            className="secondary"
            onClick={onPrint}
            title="Print PDF"
            aria-label="Print PDF"
            style={{ padding: "2px 6px", display: "inline-flex", alignItems: "center" }}
          >
            <Printer size={12} />
          </button>
        ) : null}
      </span>
      {number ? <span style={{ fontSize: "11px", color: "var(--color-muted, #667085)" }}>{number}</span> : null}
    </div>
  );
};

export default SalesInvoices;
